import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibSpinner } from '@chewy/kib-interstitials-react'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { ButtonWithLoader, type Nil, Utils } from '@pbt/pbt-ui-components'

import ItemHistoryLabel from '~/components/common/labels/ItemHistoryLabel'
import { useTaskMarkAsDoneDisabled } from '~/hooks/useTaskMarkAsDoneDisabled'
import { useTaskEvent } from '~/store/hooks/tasks'
import { getTasksIsDeleting, getTasksIsLoading } from '~/store/reducers/tasks'
import type { Task } from '~/types'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'

const useStyles = makeStyles(
  (theme) => ({
    button: {
      minWidth: 150,
      '&:not(:first-of-type)': {
        marginLeft: theme.spacing(2),
      },
    },
    itemHistoryLabel: {
      marginBottom: theme.spacing(1),
    },
  }),
  { name: 'TaskDialogActions' },
)

export interface TaskDialogActionsProps {
  isChirp?: boolean
  onDelete: () => void
  onProceed: (stateId?: string) => void
  task: Task | Nil
}

const TaskDialogActions = ({
  isChirp = false,
  task,
  onProceed,
  onDelete,
}: TaskDialogActionsProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Tasks'])

  const isLoading = useSelector(getTasksIsLoading)
  const isDeleting = useSelector(getTasksIsDeleting)

  const isContextItem = useIsCurrentContextItem(task)

  const TaskType = useTaskEvent()
  const TaskStates = TaskType.states
  const OpenState = Utils.findConstantIdByName('Open', TaskStates)
  const DoneState = Utils.findConstantIdByName('Done', TaskStates)
  const isDone = task?.stateId === DoneState
  const isCreate = !task?.id
  const isMarkAsDoneDisabled = useTaskMarkAsDoneDisabled(task)

  const markAsOpen = () => onProceed(OpenState)

  const markAsDone = () => onProceed(DoneState)

  const Button = isChirp ? KibButtonNew : ButtonWithLoader

  return (
    <Grid container item px={1}>
      <ItemHistoryLabel className={classes.itemHistoryLabel} item={task} />
      <Button
        className={classes.button}
        disabled={isLoading}
        icon={isChirp && isLoading ? <KibSpinner /> : undefined}
        loading={isLoading}
        onClick={() => onProceed()}
      >
        {isCreate ? t('Common:ADD_ACTION') : t('Common:SAVE_ACTION')}
      </Button>
      {!isCreate && (
        <>
          {isDone && (
            <Button
              className={classes.button}
              color={isChirp ? undefined : 'secondary'}
              disabled={isLoading}
              emphasis={isChirp ? 'secondary' : undefined}
              icon={isChirp && isLoading ? <KibSpinner /> : undefined}
              loading={isLoading}
              theme={isChirp ? 'action' : undefined}
              onClick={markAsOpen}
            >
              {t('Tasks:LABEL.MARK_AS_TO-DO')}
            </Button>
          )}
          {!isDone && (
            <Button
              className={classes.button}
              disabled={isLoading || isMarkAsDoneDisabled}
              icon={isChirp && isLoading ? <KibSpinner /> : undefined}
              loading={isLoading}
              onClick={markAsDone}
            >
              {t('Tasks:LABEL.MARK_AS_DONE')}
            </Button>
          )}
          {isContextItem && (
            <Button
              className={classes.button}
              color={isChirp ? undefined : 'secondary'}
              disabled={isLoading || isDeleting}
              icon={isChirp && isDeleting ? <KibSpinner /> : undefined}
              loading={isDeleting}
              theme={isChirp ? 'destructive' : undefined}
              onClick={onDelete}
            >
              {t('Common:DELETE_ACTION')}
            </Button>
          )}
        </>
      )}
    </Grid>
  )
}

export default TaskDialogActions
